import React, { useState, useEffect, useMemo } from 'react';
import { 
  Wifi, Gift, AlertCircle, CheckCircle, Edit2, Search, RefreshCw,
  MapPin, HelpCircle, ChevronRight, DollarSign, Zap, Calendar, Info,
  ExternalLink, Phone, MessageSquare, Loader2, X
} from 'lucide-react';
import { useProfile } from '../ProfileContext';
import SectionWrapper from '../components/SectionWrapper';
import { supabase } from '../../../lib/supabase';
import { getISPsForZip, getStateFromZip, getISPByCode } from '../../../utils/ispCoverage';
import { getISPUrls } from '../../../utils/providerUrls';

const ISPSection = () => {
  const {
    profile,
    refData,
    expandedSections,
    saving,
    activePerks,
    updateISP,
    updateZipCode,
    saveProfile,
    toggleSection,
    isSectionDirty,
  } = useProfile();

  const isExpanded = expandedSections.includes('isp');
  const isDirty = isSectionDirty('isp') || isSectionDirty('profile');

  // Local state for ISP data
  const [ispCoverage, setIspCoverage] = useState([]);
  const [ispPlans, setIspPlans] = useState([]);
  const [allAreaISPs, setAllAreaISPs] = useState([]);
  const [dataLastUpdated, setDataLastUpdated] = useState(null);
  const [loadingISPs, setLoadingISPs] = useState(false);
  const [ispUrls, setIspUrls] = useState(null);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBetterPlansModal, setShowBetterPlansModal] = useState(false);
  const [showCompareProvidersModal, setShowCompareProvidersModal] = useState(false);

  // Load ISP URLs when provider changes
  useEffect(() => {
    const loadUrls = async () => {
      if (profile.internetProvider) {
        const urls = await getISPUrls(profile.internetProvider);
        setIspUrls(urls);
      } else {
        setIspUrls(null);
      }
    };
    loadUrls();
  }, [profile.internetProvider]);

  // Load ISP coverage when zip code changes - using tiered approach
  useEffect(() => {
    if (profile.zipCode?.length === 5) {
      loadISPData(profile.zipCode);
    }
  }, [profile.zipCode]);

  const loadISPData = async (zip) => {
    setLoadingISPs(true);
    try {
      // Use tiered coverage lookup (national + state-level + metro)
      const tieredISPs = getISPsForZip(zip);
      
      // Convert to flat list format for compatibility
      const flatList = [
        // Fiber providers first (most desirable)
        ...tieredISPs.regional.filter(i => i.type === 'fiber').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: 'fiber',
          coverage_quality: i.quality,
          check_url: i.checkUrl
        })),
        // Metro-specific fiber (Google Fiber, etc.)
        ...tieredISPs.metro.filter(i => i.type === 'fiber').map(i => ({
          isp_name: `${i.name} (${i.metro})`,
          isp_code: i.code,
          coverage_type: 'fiber',
          coverage_quality: i.quality,
          metro: i.metro,
          check_url: i.checkUrl
        })),
        // Cable providers
        ...tieredISPs.regional.filter(i => i.type === 'cable').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: 'cable',
          coverage_quality: i.quality,
          check_url: i.checkUrl
        })),
        // 5G Fixed Wireless
        ...tieredISPs.national.filter(i => i.type === 'fixed_wireless').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: '5G wireless',
          coverage_quality: i.quality,
          note: i.note,
          check_url: i.checkUrl
        })),
        // Satellite (always available as fallback)
        ...tieredISPs.national.filter(i => i.type === 'satellite').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: 'satellite',
          coverage_quality: i.quality,
          note: i.note,
          check_url: i.checkUrl
        }))
      ];

      setIspCoverage(flatList);
      setAllAreaISPs(flatList);
      
      // Get update timestamp from database
      const { data: updateInfo } = await supabase
        .from('reference_data_updates')
        .select('updated_at')
        .eq('table_name', 'isp_service_areas')
        .single();
      setDataLastUpdated(updateInfo?.updated_at);

      // Load plans for current ISP if set
      if (profile.internetProvider) {
        const ispCode = getISPCode(profile.internetProvider, flatList);
        if (ispCode) {
          const { data: plans } = await supabase
            .from('isp_plans')
            .select('*')
            .eq('isp_code', ispCode)
            .eq('is_current', true)
            .order('sort_order');
          setIspPlans(plans || []);
        }
      }
    } catch (error) {
      console.error('Error loading ISP data:', error);
    } finally {
      setLoadingISPs(false);
    }
  };

  const getISPCode = (ispName, coverage = ispCoverage) => {
    // Handle display names that include metro info like "Google Fiber (Austin)"
    const baseName = ispName?.split(' (')[0];
    const match = coverage.find(isp => 
      isp.isp_name?.toLowerCase() === ispName?.toLowerCase() ||
      isp.isp_name?.toLowerCase().startsWith(baseName?.toLowerCase())
    );
    return match?.isp_code || ispName?.toLowerCase().replace(/\s+/g, '_');
  };

  // Get unique ISPs from reference data (fallback)
  const ispOptions = useMemo(() => {
    if (ispCoverage.length > 0) {
      return ispCoverage;
    }

    const ispSet = new Set();
    refData.ispPerks?.forEach(perk => {
      if (perk.isp_name) ispSet.add(perk.isp_name);
    });

    if (ispSet.size > 0) {
      return Array.from(ispSet).sort().map(name => ({
        isp_name: name,
        isp_code: name.toLowerCase().replace(/\s+/g, '_')
      }));
    }

    return [
      { isp_name: 'Xfinity', isp_code: 'xfinity' },
      { isp_name: 'Spectrum', isp_code: 'spectrum' },
      { isp_name: 'AT&T Fiber', isp_code: 'att_fiber' },
      { isp_name: 'Verizon Fios', isp_code: 'verizon_fios' },
      { isp_name: 'CenturyLink', isp_code: 'centurylink' },
      { isp_name: 'T-Mobile 5G Home Internet', isp_code: 'tmobile_home' },
    ];
  }, [ispCoverage, refData.ispPerks]);

  // Get perks for current ISP
  const currentISPPerks = useMemo(() => {
    if (!profile.internetProvider) return [];
    
    return refData.ispPerks?.filter(perk => 
      perk.isp_name?.toLowerCase() === profile.internetProvider?.toLowerCase() &&
      perk.service_name && 
      perk.service_name.toLowerCase() !== 'none' &&
      perk.service_name.toLowerCase() !== 'n/a'
    ) || [];
  }, [profile.internetProvider, refData.ispPerks]);

  // Get active ISP perks
  const activeISPPerks = useMemo(() => {
    if (!profile.internetProvider) return [];
    
    const ispLower = profile.internetProvider.toLowerCase();
    return activePerks.filter(perk => {
      const source = perk.perk_source?.toLowerCase() || '';
      return source.includes(ispLower) || 
             source.includes(ispLower.replace(/\s+/g, '_')) ||
             source.includes(ispLower.split(' ')[0]);
    });
  }, [activePerks, profile.internetProvider]);

  // Calculate unclaimed perks
  const unclaimedPerks = useMemo(() => {
    return currentISPPerks.filter(refPerk => 
      !activeISPPerks.some(activePerk => 
        activePerk.service_name?.toLowerCase() === refPerk.service_name?.toLowerCase()
      )
    );
  }, [currentISPPerks, activeISPPerks]);

  // Calculate streaming value
  const totalStreamingValue = useMemo(() => {
    return activeISPPerks.reduce((total, perk) => {
      const refPerk = currentISPPerks.find(p => 
        p.service_name?.toLowerCase() === perk.service_name?.toLowerCase()
      );
      return total + (parseFloat(refPerk?.monthly_value) || 0);
    }, 0);
  }, [activeISPPerks, currentISPPerks]);

  // Get current plan info
  const currentPlanInfo = useMemo(() => {
    if (!profile.internetPlan || !ispPlans.length) return null;
    return ispPlans.find(p => p.plan_name === profile.internetPlan);
  }, [profile.internetPlan, ispPlans]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Build summary
  let summary = profile.internetProvider || 'Not set';
  if (profile.internetProvider) {
    const parts = [profile.internetProvider];
    if (profile.internetPlan) parts.push(profile.internetPlan);
    if (activeISPPerks.length > 0) {
      parts.push(`${activeISPPerks.length} perk${activeISPPerks.length > 1 ? 's' : ''}`);
    }
    summary = parts.join(' • ');
  }

  return (
    <SectionWrapper
      title="Internet Provider"
      icon={Wifi}
      isExpanded={isExpanded}
      onToggle={() => toggleSection('isp')}
      isDirty={isDirty}
      isSaving={saving}
      onSave={saveProfile}
      summary={summary}
    >
      <div className="space-y-6">
        {/* Current ISP Display */}
        {profile.internetProvider ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">{profile.internetProvider}</h4>
                </div>
                {profile.internetPlan && (
                  <p className="text-sm text-gray-600 mt-1">
                    Plan: {profile.internetPlan}
                    {currentPlanInfo && (
                      <span className="text-gray-400 ml-2">
                        ({currentPlanInfo.speed_down_mbps} Mbps)
                      </span>
                    )}
                  </p>
                )}
                {profile.internetMonthlyCost > 0 && (
                  <p className="text-sm text-gray-600">
                    ${parseFloat(profile.internetMonthlyCost).toFixed(2)}/mo
                  </p>
                )}
                {totalStreamingValue > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    <Gift className="w-4 h-4 inline mr-1" />
                    ${totalStreamingValue.toFixed(2)}/mo in streaming perks
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowEditModal(true)}
                className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-blue-800 font-medium">
                  Set your internet provider to discover free streaming perks!
                </p>
                <p className="text-xs text-blue-700 mt-1 mb-3">
                  Many ISPs include free access to services like Peacock, Netflix, or Apple TV+.
                </p>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Add Internet Provider
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Streaming Perks */}
        {profile.internetProvider && currentISPPerks.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-5 h-5 text-green-600" />
              <h4 className="font-medium text-green-900">
                Streaming Perks from {profile.internetProvider}
              </h4>
            </div>
            
            <ul className="space-y-2">
              {currentISPPerks.map((perk, index) => {
                const isActive = activeISPPerks.some(ap => 
                  ap.service_name?.toLowerCase() === perk.service_name?.toLowerCase()
                );
                
                return (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    {isActive ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <span className="w-4 h-4 rounded-full border-2 border-gray-300" />
                    )}
                    <span className={isActive ? 'text-green-800 font-medium' : 'text-gray-600'}>
                      {perk.service_name}
                      {perk.included_tier && <span className="text-gray-500"> ({perk.included_tier})</span>}
                    </span>
                    {perk.monthly_value > 0 && (
                      <span className="text-xs text-gray-400 ml-auto">
                        ${parseFloat(perk.monthly_value).toFixed(2)}/mo value
                      </span>
                    )}
                    {isActive && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        Active
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            {unclaimedPerks.length > 0 && (
              <p className="mt-3 text-xs text-green-700 bg-green-100 p-2 rounded">
                💡 {unclaimedPerks.length} perk{unclaimedPerks.length > 1 ? 's' : ''} available! 
                Contact {profile.internetProvider} or check your account to activate.
              </p>
            )}

            {unclaimedPerks.length === 0 && activeISPPerks.length > 0 && (
              <p className="mt-3 text-xs text-green-700 bg-green-100 p-2 rounded">
                ✓ You've activated all available perks from {profile.internetProvider}!
              </p>
            )}
          </div>
        )}

        {/* Optimization Options */}
        {profile.internetProvider && profile.zipCode && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">💡 Optimization Options</h4>
            
            <button
              onClick={() => setShowBetterPlansModal(true)}
              className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Check for better {profile.internetProvider} plans</p>
                  <p className="text-sm text-gray-500">See if you can get more perks or lower price</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            <button
              onClick={() => setShowCompareProvidersModal(true)}
              className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Search className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Compare all providers in your area</p>
                  <p className="text-sm text-gray-500">Find the best internet + streaming deals at {profile.zipCode}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        )}

        {dataLastUpdated && profile.zipCode && (
          <p className="text-xs text-gray-400 text-center">
            Provider data last updated: {formatDate(dataLastUpdated)}
          </p>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditISPModal
          profile={profile}
          ispOptions={ispOptions}
          ispPlans={ispPlans}
          refData={refData}
          dataLastUpdated={dataLastUpdated}
          onClose={() => setShowEditModal(false)}
          onSave={async (data) => {
            // Pass data directly to saveProfile to avoid state timing issues
            await saveProfile({
              zipCode: data.zipCode,
              internetProvider: data.internetProvider,
              internetPlan: data.internetPlan,
              internetMonthlyCost: data.monthlyCost,
            });
            setShowEditModal(false);
          }}
        />
      )}

      {/* Better Plans Modal */}
      {showBetterPlansModal && (
        <BetterPlansModal
          profile={profile}
          currentPlan={currentPlanInfo}
          ispPlans={ispPlans}
          currentISPPerks={currentISPPerks}
          onClose={() => setShowBetterPlansModal(false)}
        />
      )}

      {/* Compare Providers Modal */}
      {showCompareProvidersModal && (
        <CompareProvidersModal
          profile={profile}
          allAreaISPs={allAreaISPs}
          currentISPPerks={currentISPPerks}
          refData={refData}
          onClose={() => setShowCompareProvidersModal(false)}
        />
      )}
    </SectionWrapper>
  );
};

// Edit ISP Modal Component
const EditISPModal = ({ profile, ispOptions, refData, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    zipCode: profile.zipCode || '',
    internetProvider: profile.internetProvider || '',
    internetPlan: profile.internetPlan || '',
    monthlyCost: profile.internetMonthlyCost || ''
  });
  const [localISPCoverage, setLocalISPCoverage] = useState([]);
  const [localISPPlans, setLocalISPPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPlanHelp, setShowPlanHelp] = useState(false);

  useEffect(() => {
    if (formData.zipCode.length === 5) {
      loadCoverage(formData.zipCode);
    }
  }, [formData.zipCode]);

  useEffect(() => {
    if (formData.internetProvider && formData.internetProvider !== 'other' && formData.internetProvider !== 'none') {
      loadPlans(formData.internetProvider);
    } else {
      setLocalISPPlans([]);
    }
  }, [formData.internetProvider, localISPCoverage]);

  const loadCoverage = async (zip) => {
    setLoading(true);
    try {
      // Use tiered coverage lookup
      const tieredISPs = getISPsForZip(zip);
      
      // Convert to flat list format, grouped by type for display
      const flatList = [
        // Fiber providers first
        ...tieredISPs.regional.filter(i => i.type === 'fiber').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: 'fiber',
          coverage_quality: i.quality,
          check_url: i.checkUrl
        })),
        // Metro-specific fiber
        ...tieredISPs.metro.filter(i => i.type === 'fiber').map(i => ({
          isp_name: `${i.name} (${i.metro})`,
          isp_code: i.code,
          coverage_type: 'fiber',
          coverage_quality: i.quality,
          metro: i.metro,
          check_url: i.checkUrl
        })),
        // Cable providers
        ...tieredISPs.regional.filter(i => i.type === 'cable').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: 'cable',
          coverage_quality: i.quality,
          check_url: i.checkUrl
        })),
        // 5G Fixed Wireless
        ...tieredISPs.national.filter(i => i.type === 'fixed_wireless').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: '5G wireless',
          coverage_quality: i.quality,
          check_url: i.checkUrl
        })),
        // Satellite
        ...tieredISPs.national.filter(i => i.type === 'satellite').map(i => ({
          isp_name: i.name,
          isp_code: i.code,
          coverage_type: 'satellite',
          coverage_quality: i.quality,
          check_url: i.checkUrl
        }))
      ];
      
      setLocalISPCoverage(flatList);
    } catch (error) {
      console.error('Error loading coverage:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async (ispName) => {
    const isp = localISPCoverage.find(i => i.isp_name === ispName);
    if (isp?.isp_code) {
      const { data: plans } = await supabase
        .from('isp_plans')
        .select('*')
        .eq('isp_code', isp.isp_code)
        .eq('is_current', true)
        .order('sort_order');
      setLocalISPPlans(plans || []);
    }
  };

  const displayISPs = localISPCoverage.length > 0 ? localISPCoverage : ispOptions;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Edit Internet Provider</h3>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="w-4 h-4 inline mr-1" />
              Zip Code
            </label>
            <input
              type="text"
              value={formData.zipCode}
              onChange={(e) => setFormData({ ...formData, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5), internetProvider: '', internetPlan: '' })}
              placeholder="Enter 5-digit zip code"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              maxLength="5"
            />
          </div>

          {formData.zipCode.length === 5 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Internet Provider
              </label>
              {loading ? (
                <div className="flex items-center justify-center py-4 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Finding providers...
                </div>
              ) : (
                <>
                  <select
                    value={formData.internetProvider}
                    onChange={(e) => setFormData({ ...formData, internetProvider: e.target.value, internetPlan: '' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select your provider...</option>
                    {displayISPs.map(isp => (
                      <option key={isp.isp_code || isp.isp_name} value={isp.isp_name}>
                        {isp.isp_name}
                        {isp.coverage_type && ` (${isp.coverage_type})`}
                      </option>
                    ))}
                    <option value="other">Other / Not listed</option>
                    <option value="none">No home internet</option>
                  </select>
                  
                  {/* Check availability link */}
                  {formData.internetProvider && formData.internetProvider !== 'other' && formData.internetProvider !== 'none' && (
                    (() => {
                      const selectedISP = displayISPs.find(i => i.isp_name === formData.internetProvider);
                      return selectedISP?.check_url ? (
                        <a
                          href={selectedISP.check_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Verify availability at your address
                        </a>
                      ) : null;
                    })()
                  )}
                  
                  {/* Coverage quality note */}
                  {formData.internetProvider && formData.internetProvider !== 'other' && formData.internetProvider !== 'none' && (
                    (() => {
                      const selectedISP = displayISPs.find(i => i.isp_name === formData.internetProvider);
                      if (selectedISP?.coverage_quality === 'partial') {
                        return (
                          <p className="mt-2 text-xs text-amber-600">
                            <AlertCircle className="w-3 h-3 inline mr-1" />
                            Partial coverage in your state - verify availability at your address
                          </p>
                        );
                      }
                      if (selectedISP?.coverage_quality === 'expanding') {
                        return (
                          <p className="mt-2 text-xs text-blue-600">
                            <Info className="w-3 h-3 inline mr-1" />
                            Expanding in your area - check for availability
                          </p>
                        );
                      }
                      return null;
                    })()
                  )}
                </>
              )}
            </div>
          )}

          {formData.internetProvider && formData.internetProvider !== 'other' && formData.internetProvider !== 'none' && localISPPlans.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Your Plan</label>
                <button
                  onClick={() => setShowPlanHelp(!showPlanHelp)}
                  className="text-purple-600 hover:text-purple-800 text-xs flex items-center gap-1"
                >
                  <HelpCircle className="w-3 h-3" />
                  Not sure?
                </button>
              </div>

              {showPlanHelp && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2 text-sm">
                  <p className="font-medium text-yellow-800 mb-1">How to find your plan:</p>
                  <ul className="text-yellow-700 text-xs space-y-1 list-disc list-inside">
                    <li>Check your monthly bill</li>
                    <li>Log into your provider's website</li>
                    <li>Look for "My Plan" or "Account"</li>
                  </ul>
                  <p className="mt-2 text-yellow-800 italic text-xs">🎯 You only have to do this once!</p>
                </div>
              )}

              <select
                value={formData.internetPlan}
                onChange={(e) => setFormData({ ...formData, internetPlan: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select your plan...</option>
                {localISPPlans.map(plan => (
                  <option key={plan.id} value={plan.plan_name}>
                    {plan.plan_name} - {plan.speed_down_mbps} Mbps - ${plan.monthly_price}/mo
                  </option>
                ))}
                <option value="unknown">I'm not sure</option>
                <option value="grandfathered">Older plan not listed</option>
              </select>
            </div>
          )}

          {formData.internetProvider && formData.internetProvider !== 'none' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Cost (optional)</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.monthlyCost}
                  onChange={(e) => setFormData({ ...formData, monthlyCost: e.target.value })}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Enter what you actually pay (including fees/taxes)</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(formData)}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Better Plans Modal Component - Phase 2 Enhanced
const BetterPlansModal = ({ profile, currentPlan, ispPlans, currentISPPerks, onClose }) => {
  const [showNegotiationTips, setShowNegotiationTips] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [ispUrls, setIspUrls] = useState(null);

  // Load ISP URLs on mount
  useEffect(() => {
    const loadUrls = async () => {
      if (profile.internetProvider) {
        const urls = await getISPUrls(profile.internetProvider);
        setIspUrls(urls);
      }
    };
    loadUrls();
  }, [profile.internetProvider]);

  // Calculate streaming value from ISP perks
  const streamingValue = currentISPPerks.reduce((total, perk) => 
    total + (parseFloat(perk.monthly_value) || 0), 0
  );

  // Get user's actual cost (from profile or plan price)
  const currentCost = profile.internetMonthlyCost || currentPlan?.monthly_price || 0;
  const effectiveCost = currentCost - streamingValue;

  // Derive contact info from loaded ISP URLs
  const contactInfo = ispUrls ? {
    phone: ispUrls.phone,
    website: ispUrls.account?.replace('https://', ''),
    signupUrl: ispUrls.signup,
    chat: ispUrls.hasChat ?? true,
  } : { 
    phone: null, 
    website: null, 
    signupUrl: null,
    chat: false 
  };

  // Categorize plans
  const categorizePlans = () => {
    const categories = {
      saveMoney: [],
      moreSpeed: [],
      promoDeals: [],
    };

    ispPlans.forEach(plan => {
      if (currentPlan && plan.plan_name === currentPlan.plan_name) return;

      const planEffectiveCost = plan.monthly_price - streamingValue;
      const hasPromo = plan.promo_price && plan.promo_price < plan.monthly_price;

      if (currentPlan && 
          plan.monthly_price < currentPlan.monthly_price && 
          plan.speed_down_mbps >= currentPlan.speed_down_mbps * 0.8) {
        categories.saveMoney.push({
          ...plan,
          savings: currentPlan.monthly_price - plan.monthly_price,
          savingsAnnual: (currentPlan.monthly_price - plan.monthly_price) * 12,
          effectiveCost: planEffectiveCost,
          reason: 'save'
        });
      }

      if (currentPlan && 
          plan.speed_down_mbps > currentPlan.speed_down_mbps * 1.25 &&
          plan.monthly_price <= currentPlan.monthly_price * 1.15) {
        categories.moreSpeed.push({
          ...plan,
          speedIncrease: plan.speed_down_mbps - currentPlan.speed_down_mbps,
          speedMultiplier: (plan.speed_down_mbps / currentPlan.speed_down_mbps).toFixed(1),
          effectiveCost: planEffectiveCost,
          reason: 'speed'
        });
      }

      if (hasPromo) {
        categories.promoDeals.push({
          ...plan,
          promoSavings: plan.monthly_price - plan.promo_price,
          promoSavingsTotal: (plan.monthly_price - plan.promo_price) * (plan.promo_duration_months || 12),
          effectiveCost: plan.promo_price - streamingValue,
          reason: 'promo'
        });
      }
    });

    categories.saveMoney.sort((a, b) => b.savings - a.savings);
    categories.moreSpeed.sort((a, b) => b.speedIncrease - a.speedIncrease);
    categories.promoDeals.sort((a, b) => b.promoSavingsTotal - a.promoSavingsTotal);

    return categories;
  };

  const categories = categorizePlans();
  const hasRecommendations = 
    categories.saveMoney.length > 0 || 
    categories.moreSpeed.length > 0 || 
    categories.promoDeals.length > 0;

  const getAllPlans = () => {
    const seen = new Set();
    const all = [];
    [...categories.saveMoney, ...categories.moreSpeed, ...categories.promoDeals].forEach(plan => {
      if (!seen.has(plan.id)) {
        seen.add(plan.id);
        all.push(plan);
      }
    });
    return all;
  };

  const getDisplayPlans = () => {
    switch (selectedCategory) {
      case 'save': return categories.saveMoney;
      case 'speed': return categories.moreSpeed;
      case 'promo': return categories.promoDeals;
      default: return getAllPlans();
    }
  };

  const displayPlans = getDisplayPlans();

  const PlanCard = ({ plan, highlight }) => (
    <div className={`border rounded-lg p-4 transition-all ${
      highlight ? 'border-green-400 bg-green-50 ring-2 ring-green-200' : 'border-gray-200 hover:border-purple-300'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-gray-900">{plan.plan_name}</h4>
            {plan.reason === 'save' && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                💰 Save ${plan.savings?.toFixed(0)}/mo
              </span>
            )}
            {plan.reason === 'speed' && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                ⚡ {plan.speedMultiplier}x faster
              </span>
            )}
            {plan.reason === 'promo' && (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                🎁 Promo available
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {plan.speed_down_mbps} Mbps down / {plan.speed_up_mbps || '?'} Mbps up
          </p>
          <p className="text-xs text-gray-500">
            {plan.data_cap_gb ? `${plan.data_cap_gb} GB data cap` : '✓ Unlimited data'}
            {plan.contract_required && ' • Contract required'}
          </p>
        </div>
        <div className="text-right ml-4">
          <p className="font-bold text-gray-900 text-lg">${plan.monthly_price}/mo</p>
          {plan.promo_price && (
            <p className="text-sm text-green-600 font-medium">
              ${plan.promo_price}/mo for {plan.promo_duration_months || 12} mo
            </p>
          )}
          {streamingValue > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Effective: ${plan.effectiveCost?.toFixed(2)}/mo
            </p>
          )}
        </div>
      </div>

      {plan.reason === 'save' && plan.savingsAnnual > 0 && (
        <div className="mt-3 p-2 bg-green-100 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>Annual savings:</strong> ${plan.savingsAnnual.toFixed(0)}/year vs your current plan
          </p>
        </div>
      )}

      {plan.reason === 'promo' && plan.promoSavingsTotal > 0 && (
        <div className="mt-3 p-2 bg-orange-100 rounded-lg">
          <p className="text-sm text-orange-800">
            <strong>Promo savings:</strong> ${plan.promoSavingsTotal.toFixed(0)} over {plan.promo_duration_months || 12} months
          </p>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <a
          href={contactInfo.website ? `https://${contactInfo.website}` : '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors text-center"
        >
          Switch to This Plan
        </a>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Better Plans from {profile.internetProvider}
            </h3>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Your Current Plan</h4>
          {currentPlan ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">{currentPlan.plan_name}</p>
                <p className="text-sm text-gray-600">
                  {currentPlan.speed_down_mbps} Mbps • {currentPlan.data_cap_gb ? `${currentPlan.data_cap_gb} GB cap` : 'Unlimited'}
                </p>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900">${currentCost.toFixed(2)}/mo</p>
                {streamingValue > 0 && (
                  <>
                    <p className="text-xs text-green-600">- ${streamingValue.toFixed(2)} streaming value</p>
                    <p className="text-sm font-medium text-purple-600">
                      = ${effectiveCost.toFixed(2)}/mo effective
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-gray-600">No plan selected</p>
              <p className="text-sm text-gray-500">Select your plan to see personalized comparisons</p>
            </div>
          )}
        </div>

        {hasRecommendations && (
          <div className="px-6 py-3 border-b border-gray-200 flex gap-2 overflow-x-auto">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === 'all' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Options ({getAllPlans().length})
            </button>
            {categories.saveMoney.length > 0 && (
              <button
                onClick={() => setSelectedCategory('save')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === 'save' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                💰 Save Money ({categories.saveMoney.length})
              </button>
            )}
            {categories.moreSpeed.length > 0 && (
              <button
                onClick={() => setSelectedCategory('speed')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === 'speed' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                ⚡ More Speed ({categories.moreSpeed.length})
              </button>
            )}
            {categories.promoDeals.length > 0 && (
              <button
                onClick={() => setSelectedCategory('promo')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === 'promo' 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                🎁 Promo Deals ({categories.promoDeals.length})
              </button>
            )}
          </div>
        )}

        <div className="p-6">
          {hasRecommendations ? (
            <div className="space-y-4">
              {displayPlans.map((plan, index) => (
                <PlanCard 
                  key={plan.id} 
                  plan={plan} 
                  highlight={index === 0 && selectedCategory !== 'all'}
                />
              ))}
            </div>
          ) : (
            <div>
              {ispPlans.length === 0 ? (
                <div className="text-center py-8">
                  <Info className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No plan data available</h4>
                  <p className="text-gray-600 mb-4">
                    We don't have pricing information for {profile.internetProvider} in our database yet.
                  </p>
                  {contactInfo.signupUrl && (
                    <a 
                      href={contactInfo.signupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Plans on {profile.internetProvider}
                    </a>
                  )}
                </div>
              ) : (
                <>
                  {/* Show all available plans from this provider */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      All {profile.internetProvider} Plans ({ispPlans.length})
                    </h4>
                    <div className="space-y-2">
                      {ispPlans.map(plan => {
                        const isCurrentPlan = currentPlan && plan.plan_name === currentPlan.plan_name;
                        return (
                          <div 
                            key={plan.id} 
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isCurrentPlan 
                                ? 'border-purple-300 bg-purple-50' 
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{plan.plan_name}</span>
                                {isCurrentPlan && (
                                  <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                                    Current
                                  </span>
                                )}
                              </div>
                              <span className="text-sm text-gray-500">
                                {plan.speed_down_mbps} Mbps • {plan.data_cap_gb ? `${plan.data_cap_gb} GB` : 'Unlimited'}
                              </span>
                            </div>
                            <div className="text-right">
                              <span className="font-medium text-gray-900">${plan.monthly_price}/mo</span>
                              {plan.promo_price && (
                                <p className="text-xs text-green-600">${plan.promo_price}/mo promo</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Link to provider */}
                  {contactInfo.signupUrl && (
                    <div className="text-center pt-4 border-t border-gray-200">
                      <a 
                        href={contactInfo.signupUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Change Plan on {profile.internetProvider}
                      </a>
                      <p className="text-xs text-gray-500 mt-2">
                        Contact your provider to switch plans or negotiate pricing
                      </p>
                    </div>
                  )}
                </>
              )}
              {streamingValue > 0 && (
                <div className="mt-4 inline-block bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                  <p className="text-sm text-green-800">
                    <Gift className="w-4 h-4 inline mr-1" />
                    Your plan includes <strong>${streamingValue.toFixed(2)}/mo</strong> in streaming perks.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowNegotiationTips(!showNegotiationTips)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="font-medium text-gray-900">💡 Tips to lower your bill</span>
              <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showNegotiationTips ? 'rotate-90' : ''}`} />
            </button>
            
            {showNegotiationTips && (
              <div className="mt-4 space-y-3">
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="font-medium text-yellow-800 text-sm">🎯 Ask for the retention department</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Say "I'm thinking of canceling" to get transferred to retention, where reps have authority to offer better deals.
                  </p>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="font-medium text-blue-800 text-sm">📊 Mention competitor pricing</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Research competitor prices in your area and mention them. ISPs often match or beat competitor offers.
                  </p>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-medium text-green-800 text-sm">📅 Call at the right time</p>
                  <p className="text-xs text-green-700 mt-1">
                    End of month or quarter is best - reps may be more flexible to meet quotas. Avoid Mondays and lunch hours.
                  </p>
                </div>
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="font-medium text-purple-800 text-sm">📦 Ask about unadvertised bundles</p>
                  <p className="text-xs text-purple-700 mt-1">
                    Many ISPs have loyalty discounts or bundles not shown online. Ask "What promotions are available for existing customers?"
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-3">Contact {profile.internetProvider}</p>
            <div className="flex flex-wrap gap-3">
              {contactInfo.phone && (
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-sm">{contactInfo.phone}</span>
                </a>
              )}
              {contactInfo.website && (
                <a
                  href={`https://${contactInfo.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-sm">My Account</span>
                </a>
              )}
              {contactInfo.chat && (
                <a
                  href={`https://${contactInfo.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm">Live Chat</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compare Providers Modal Component
const CompareProvidersModal = ({ profile, allAreaISPs, currentISPPerks, refData, onClose }) => {
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [allPlans, setAllPlans] = useState({});
  const [ispUrlsMap, setIspUrlsMap] = useState({});

  useEffect(() => {
    loadAllPlans();
    loadAllIspUrls();
  }, []);

  const loadAllPlans = async () => {
    setLoadingPlans(true);
    try {
      const ispCodes = allAreaISPs.map(isp => isp.isp_code).filter(Boolean);
      const { data: plans } = await supabase
        .from('isp_plans')
        .select('*')
        .in('isp_code', ispCodes)
        .eq('is_current', true)
        .order('monthly_price');

      const grouped = {};
      plans?.forEach(plan => {
        if (!grouped[plan.isp_code]) grouped[plan.isp_code] = [];
        grouped[plan.isp_code].push(plan);
      });
      setAllPlans(grouped);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadAllIspUrls = async () => {
    const urlsMap = {};
    for (const isp of allAreaISPs) {
      const urls = await getISPUrls(isp.isp_name);
      if (urls) {
        urlsMap[isp.isp_name] = urls;
      }
    }
    setIspUrlsMap(urlsMap);
  };

  const getISPStreamingValue = (ispName) => {
    const perks = refData.ispPerks?.filter(p => 
      p.isp_name?.toLowerCase() === ispName?.toLowerCase()
    ) || [];
    return perks.reduce((total, perk) => total + (parseFloat(perk.monthly_value) || 0), 0);
  };

  // Get signup URL for a provider from preloaded map
  const getSignupUrl = (ispName) => {
    return ispUrlsMap[ispName]?.signup || null;
  };

  const currentStreamingValue = currentISPPerks.reduce((total, perk) => 
    total + (parseFloat(perk.monthly_value) || 0), 0
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Best Internet Deals in {profile.zipCode}</h3>
              <p className="text-sm text-gray-500 mt-1">
                Current: {profile.internetProvider} • Streaming value: ${currentStreamingValue.toFixed(2)}/mo
              </p>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {loadingPlans ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading provider options...
            </div>
          ) : (
            <div className="space-y-6">
              {allAreaISPs.map(isp => {
                const plans = allPlans[isp.isp_code] || [];
                const streamingValue = getISPStreamingValue(isp.isp_name);
                const isCurrentISP = isp.isp_name === profile.internetProvider;
                const ispPerks = refData.ispPerks?.filter(p => 
                  p.isp_name?.toLowerCase() === isp.isp_name?.toLowerCase()
                ) || [];
                
                if (plans.length === 0 && !isCurrentISP) return null;

                return (
                  <div 
                    key={isp.isp_code} 
                    className={`border rounded-lg overflow-hidden ${
                      isCurrentISP ? 'border-purple-300 bg-purple-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900">{isp.isp_name}</h4>
                          {isCurrentISP && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Current</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-gray-500 capitalize">{isp.coverage_type}</span>
                          {streamingValue > 0 && (
                            <span className="text-green-600">
                              <Gift className="w-4 h-4 inline mr-1" />${streamingValue.toFixed(2)}/mo perks
                            </span>
                          )}
                        </div>
                      </div>
                      {ispPerks.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">
                          Includes: {ispPerks.map(p => p.service_name).join(', ')}
                        </p>
                      )}
                    </div>

                    <div className="p-4">
                      {plans.length > 0 ? (
                        <div className="space-y-2">
                          {plans.slice(0, 3).map(plan => (
                            <div key={plan.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                              <div>
                                <span className="font-medium text-gray-900">{plan.plan_name}</span>
                                <span className="text-sm text-gray-500 ml-2">{plan.speed_down_mbps} Mbps</span>
                              </div>
                              <div className="text-right">
                                <span className="font-medium text-gray-900">${plan.monthly_price}/mo</span>
                                {plan.promo_price && (
                                  <span className="text-xs text-green-600 ml-2">(${plan.promo_price} promo)</span>
                                )}
                              </div>
                            </div>
                          ))}
                          {plans.length > 3 && (
                            <p className="text-xs text-gray-500 pt-2">+{plans.length - 3} more plans</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Contact provider for pricing.</p>
                      )}

                      {!isCurrentISP && getSignupUrl(isp.isp_name) && (
                        <a 
                          href={getSignupUrl(isp.isp_name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors inline-block text-center"
                        >
                          View Plans & Sign Up →
                        </a>
                      )}
                      {!isCurrentISP && !getSignupUrl(isp.isp_name) && (
                        <p className="mt-3 text-sm text-gray-500 text-center">
                          Search for "{isp.isp_name} internet plans" to learn more
                        </p>
                      )}
                      {isCurrentISP && getSignupUrl(isp.isp_name) && (
                        <a 
                          href={getSignupUrl(isp.isp_name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors inline-block text-center"
                        >
                          View Plans & Upgrade Options →
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-xs text-gray-400 text-center mt-6">
            Prices may vary. Verify with provider before switching.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ISPSection;